import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Paper,
  TextField,
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  Alert,
  Autocomplete,
  Select,
  MenuItem,
  InputLabel
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { createBogo, updateBogo, getBogoById } from '../../Webservice/offerAPI';
import { getData } from '../../Webservice/Webservice';
import PRODUCT_ROUTES from '../../API_Route/ProductRoute';
import Swal from 'sweetalert2';

const getUserId = () => {
    let userId = null;
    ['user', 'adminUser', 'commonUser', 'sellerUser'].forEach(key => {
        if (userId) return;
        const u = sessionStorage.getItem(key) || localStorage.getItem(key);
        if (u) {
            try {
                const parsed = JSON.parse(u);
                userId = parsed._id || parsed.id || null;
            } catch (e) {}
        }
    });
    return userId || sessionStorage.getItem('userId') || localStorage.getItem('userId') || localStorage.getItem('commonUserId') || '';
};

const steps = ['Basic Details', 'Buy Condition', 'Get Reward', 'Review & Submit'];

function CreateBogoOffer() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [activeStep, setActiveStep] = useState(0);
  
  // Data for Selectors
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [products, setProducts] = useState([]);

  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');

  // State for Form Data
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    visibility: ['b2b', 'b2c'],
    buyType: 'product', // 'product' or 'category'
    buyItems: [], // Array of objects
    buyQuantity: 1,
    getQuantity: 1,
    discountPercentage: 100,
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (id) {
        const fetchEditData = async () => {
            try {
                const response = await getBogoById(id);
                const bogo = response.data || response.bogo;
                if (bogo) {
                    const mappedBuyItems = bogo.buyProducts?.map(p => p.product_id || p.category_id) || [];
                    setFormData({
                        title: bogo.title || '',
                        description: bogo.description || '',
                        startDate: bogo.startDate ? bogo.startDate.split('T')[0] : '',
                        endDate: bogo.endDate ? bogo.endDate.split('T')[0] : '',
                        visibility: bogo.visibility || ['b2b', 'b2c'],
                        buyType: bogo.buyProducts && bogo.buyProducts[0] && bogo.buyProducts[0].category_id ? 'category' : 'product',
                        buyItems: mappedBuyItems,
                        buyQuantity: bogo.buyQuantity || 1,
                        getQuantity: bogo.getQuantity || 1,
                        discountPercentage: bogo.discountPercentage || 100,
                    });
                }
            } catch (error) {
                console.error('Failed to fetch BOGO for editing', error);
                Swal.fire('Error', 'Failed to load offer details', 'error');
            }
        };
        fetchEditData();
    }
  }, [id]);

  // Initial Category Fetch
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const tokenValue = sessionStorage.getItem('token');
        const token = tokenValue ? `Bearer ${tokenValue}` : '';
        const catRes = await getData('categories/list?limit=1000', null, token);
        if (catRes && catRes.categories) {
            setCategories(Array.isArray(catRes.categories) ? catRes.categories : []);
        }
      } catch (error) {
        console.error('Failed to fetch categories', error);
      }
    };
    fetchCategories();
  }, []);

  // Fetch Subcategories when Category changes
  useEffect(() => {
    const fetchSubcategories = async () => {
      if (!selectedCategory) {
          setSubcategories([]);
          return;
      }
      try {
          const tokenValue = sessionStorage.getItem('token');
          const token = tokenValue ? `Bearer ${tokenValue}` : '';
          const response = await getData(`subcategories/list?limit=1000&category_id=${selectedCategory}`, null, token);
          setSubcategories(Array.isArray(response.subcategories) ? response.subcategories : []);
      } catch (error) {
          setSubcategories([]);
      }
    };
    fetchSubcategories();
  }, [selectedCategory]);

  // Fetch Products when Category/Subcategory changes
  useEffect(() => {
    const searchProducts = async () => {
      try {
          const tokenValue = sessionStorage.getItem('token');
          const token = tokenValue ? `Bearer ${tokenValue}` : '';
          
          let queryParams = new URLSearchParams();
          queryParams.append('limit', '500'); // Fetch enough
          if (selectedCategory) queryParams.append('categoryId', selectedCategory);
          if (selectedSubcategory) queryParams.append('subcategoryId', selectedSubcategory);

          const response = await getData(`${PRODUCT_ROUTES.GET_PRODUCT}?${queryParams.toString()}`, null, token);
          setProducts(Array.isArray(response.products) ? response.products : []);
      } catch (error) {
          setProducts([]);
      }
    };
    
    // Fetch products if buyType is product
    if (formData.buyType === 'product') {
        searchProducts();
    }
  }, [selectedCategory, selectedSubcategory, formData.buyType]);

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRadioChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ 
      ...prev, 
      [name]: value,
      ...(name === 'buyType' ? { buyItems: [] } : {})
    }));
    
    // Reset filters
    setSelectedCategory('');
    setSelectedSubcategory('');
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => {
      let vis = [...prev.visibility];
      if (checked) {
        vis.push(name);
      } else {
        vis = vis.filter((v) => v !== name);
      }
      return { ...prev, visibility: vis };
    });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Both buy and get use the EXACT same products in this strictly enforced BOGO logic
      const targetProducts = formData.buyItems.map(item => ({
        product_id: formData.buyType === 'product' ? item._id : null,
        category_id: formData.buyType === 'category' ? item._id : null,
      }));

      const userId = getUserId();
      const payload = {
        title: formData.title,
        description: formData.description,
        startDate: formData.startDate,
        endDate: formData.endDate,
        visibility: formData.visibility,
        buyProducts: targetProducts,
        buyQuantity: formData.buyQuantity,
        getProducts: targetProducts, // STRICTLY identical to buyProducts
        getQuantity: formData.getQuantity,
        discountPercentage: formData.discountPercentage,
        isActive: true,
        createdBy: userId,
        action: isEdit ? 'Update' : 'Create',
        entity: 'BOGO Offer',
        activityLog: {
            action: isEdit ? 'Update' : 'Create',
            entity: 'BOGO Offer',
            createdBy: userId
        }
      };

      const res = isEdit ? await updateBogo(id, payload) : await createBogo(payload);
      if (res && res.success) {
        Swal.fire({
          title: 'Success',
          text: `BOGO Offer ${isEdit ? 'Updated' : 'Created'} Successfully`,
          icon: 'success'
        });
        navigate('/offers/bogo');
      } else {
        throw new Error(res?.message || `Failed to ${isEdit ? 'update' : 'create'} BOGO offer`);
      }
    } catch (error) {
      console.error(error);
      Swal.fire({
        title: 'Error',
        text: error.message || 'Something went wrong',
        icon: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField fullWidth label="Offer Title" name="title" value={formData.title} onChange={handleChange} required />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Description" name="description" value={formData.description} onChange={handleChange} multiline rows={3} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="date" label="Start Date" name="startDate" value={formData.startDate} onChange={handleChange} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="date" label="End Date" name="endDate" value={formData.endDate} onChange={handleChange} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Visibility</FormLabel>
                <FormGroup row>
                  <FormControlLabel control={<Checkbox checked={formData.visibility.includes('b2b')} onChange={handleCheckboxChange} name="b2b" />} label="B2B" />
                  <FormControlLabel control={<Checkbox checked={formData.visibility.includes('b2c')} onChange={handleCheckboxChange} name="b2c" />} label="B2C" />
                </FormGroup>
              </FormControl>
            </Grid>
          </Grid>
        );
      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ fontWeight: 700, mb: 1, color: '#1a1c1e' }}>What condition triggers the BOGO?</FormLabel>
                <RadioGroup row name="buyType" value={formData.buyType} onChange={handleRadioChange}>
                  <FormControlLabel value="product" control={<Radio />} label="Specific Products" />
                  <FormControlLabel value="category" control={<Radio />} label="Specific Categories" />
                </RadioGroup>
              </FormControl>
            </Grid>

            {/* Drilldown Selectors */}
            {formData.buyType === 'product' && (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Filter by Category</InputLabel>
                    <Select value={selectedCategory} label="Filter by Category" onChange={(e) => setSelectedCategory(e.target.value)}>
                      <MenuItem value=""><em>All Categories</em></MenuItem>
                      {categories.map(cat => (
                        <MenuItem key={cat._id} value={cat._id}>{cat.title}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth disabled={!selectedCategory}>
                    <InputLabel>Filter by Subcategory</InputLabel>
                    <Select value={selectedSubcategory} label="Filter by Subcategory" onChange={(e) => setSelectedSubcategory(e.target.value)}>
                      <MenuItem value=""><em>All Subcategories</em></MenuItem>
                      {subcategories.map(sub => (
                        <MenuItem key={sub._id} value={sub._id}>{sub.title}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={formData.buyType === 'product' ? products : categories}
                getOptionLabel={(option) => option.title || ''}
                value={formData.buyItems}
                onChange={(event, newValue) => {
                  setFormData(prev => ({ ...prev, buyItems: newValue }));
                }}
                isOptionEqualToValue={(option, value) => option._id === value._id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label={`Select ${formData.buyType === 'product' ? 'Products' : 'Categories'}`}
                    placeholder={`Search ${formData.buyType}...`}
                    required={formData.buyItems.length === 0}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="number" label="Quantity Required to Buy" name="buyQuantity" value={formData.buyQuantity} onChange={handleChange} required inputProps={{ min: 1 }} />
            </Grid>
          </Grid>
        );
      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 1, '& .MuiAlert-message': { width: '100%' } }}>
                    <Typography variant="body1" fontWeight={700}>
                        Strict BOGO Rule Applied: 
                    </Typography>
                    <Typography variant="body2">
                        The reward will automatically be the EXACT same {formData.buyType}(s) chosen in Step 1. You cannot mix different items in this flow.
                    </Typography>
                </Alert>
                <Paper sx={{ p: 2, bgcolor: '#f8fafc', border: '1px dashed #cbd5e1', mt: 2 }}>
                    <Typography color="text.secondary" variant="body2" mb={1}>Inherited Target(s):</Typography>
                    <Typography fontWeight={600} color="#0d47a1">
                        {formData.buyItems.length > 0 ? formData.buyItems.map(i => i.title).join(', ') : 'No items selected yet'}
                    </Typography>
                </Paper>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="number" label="Quantity to Give (Reward)" name="getQuantity" value={formData.getQuantity} onChange={handleChange} required inputProps={{ min: 1 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="number" label="Discount Percentage" name="discountPercentage" value={formData.discountPercentage} onChange={handleChange} required inputProps={{ min: 1, max: 100 }} helperText="100% means it is completely free" />
            </Grid>
          </Grid>
        );
      case 3:
        return (
          <Box>
            <Alert severity="success" sx={{ mb: 3 }}>
              Please review your strict BOGO offer before submitting.
            </Alert>
            <Typography variant="h6" mb={2}>Offer Details</Typography>
            <Paper elevation={0} sx={{ p: 2, bgcolor: '#f1f5f9', mb: 2 }}>
                <Typography><b>Title:</b> {formData.title}</Typography>
                <Typography><b>Visibility:</b> {formData.visibility.join(', ').toUpperCase()}</Typography>
            </Paper>
            <Paper elevation={0} sx={{ p: 2, bgcolor: '#e0e7ff', mb: 2 }}>
                <Typography><b>The Condition:</b> Buy {formData.buyQuantity} of the following {formData.buyType}(s):</Typography>
                <Typography color="text.secondary" variant="body2" mt={1}>
                    {formData.buyItems.map(i => i.title).join(', ')}
                </Typography>
            </Paper>
            <Paper elevation={0} sx={{ p: 2, bgcolor: '#dcfce7', mb: 2 }}>
                <Typography><b>The Reward:</b> Get {formData.getQuantity} of the exact same {formData.buyType}(s) at {formData.discountPercentage}% off!</Typography>
            </Paper>
          </Box>
        );
      default:
        return 'Unknown step';
    }
  };

  return (
    <Box p={3} sx={{ backgroundColor: '#f8fafc', minHeight: '80vh', borderRadius: '10px' }}>
      <Typography variant="h4" mb={4} fontWeight={800} fontFamily="Mulish, sans-serif">
        Create Strict BOGO Offer
      </Typography>
      
      <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px solid #e2e8f0' }}>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 5 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ minHeight: '300px' }}>
          {renderStepContent(activeStep)}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 2, borderTop: '1px solid #e2e8f0' }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            variant="outlined"
            sx={{ fontWeight: 700 }}
          >
            Back
          </Button>
          
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={loading || formData.buyItems.length === 0}
              sx={{ fontWeight: 700, backgroundColor: '#0d47a1', color: '#fff' }}
            >
              {loading ? 'Submitting...' : 'Create Offer'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              sx={{ fontWeight: 700, backgroundColor: 'rgb(255, 209, 2)', color: 'black' }}
            >
              Next
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
}

export default CreateBogoOffer;
